import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Columns3, Tag } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { LeadFilters } from "@/components/LeadFilters";
import { BroadcastButton } from "@/components/broadcasts/BroadcastButton";
import { CreateLeadModal } from "@/components/CreateLeadModal";
import { EditLeadModal } from "@/components/EditLeadModal";
import { ManageColumnsModal } from "@/components/ManageColumnsModal";
import { LabelsProvider } from "@/components/labels/LabelsProvider";
import { BoardView } from "@/components/leads-pipeline/BoardView";
import { TableView } from "@/components/leads-pipeline/TableView";
import { ViewModeToggle } from "@/components/leads-pipeline/ViewModeToggle";
import { usePipelineLeads } from "@/hooks/usePipeline";
import { useLeads } from "@/hooks/useLeads";
import { useCurrentUserProfile } from "@/hooks/useUsers";
import { hasActiveFilters, useCrmStore } from "@/stores/crm.store";

/**
 * Pipeline de Prospectos — the single place the team works its leads.
 *
 * The board and the table were two pages until they were merged: they filter,
 * present and act on the same rows, but only the table had filters and only the
 * board got used. Both modes now read one filter set from `crm.store`, so
 * switching view never loses what someone narrowed down to.
 */
export function LeadsPipeline(): React.JSX.Element {
  const viewMode = useCrmStore((s) => s.viewMode);
  const showDeletedLeads = useCrmStore((s) => s.showDeletedLeads);
  const openCreateModal = useCrmStore((s) => s.openCreateModal);
  const openManageLabels = useCrmStore((s) => s.openManageLabels);
  const filtersActive = useCrmStore(hasActiveFilters);
  const profile = useCurrentUserProfile();
  // Column management is admin-only — a column is pipeline structure. Account
  // managers count as admins here, matching `authorizeAdmin` on the server,
  // which has always allowed them; only this check left them without the UI.
  // Labels are NOT gated: any user of the client may manage them.
  const isAdmin =
    profile?.role === "admin" ||
    profile?.role === "super_admin" ||
    profile?.role === "account_manager";

  const isBoard = viewMode === "board";

  // Only the visible mode fetches — the two share every filter, so otherwise each
  // keystroke in the search box would fire two requests.
  const { stages, grouped, counts, truncated, isLoading, isError } = usePipelineLeads({
    enabled: isBoard,
  });
  const leadsQuery = useLeads({ enabled: !isBoard && !showDeletedLeads });

  const [manageOpen, setManageOpen] = useState(false);

  // Real per-stage totals from the server. The board only loads the top slice of
  // each column, so counting the loaded cards would under-report — and
  // ManageColumnsModal uses these to block deleting a column that still holds
  // leads. Filters still shrink them (they are applied server-side), which is why
  // the button below stays disabled while any filter is active.
  const leadCounts = Object.fromEntries(
    stages.map((s) => [s.key, counts[s.key] ?? 0]),
  );

  const count = isBoard
    ? Object.values(leadCounts).reduce((n, c) => n + c, 0)
    : (leadsQuery.data?.total ?? 0);
  const truncationNotice = truncated ? (
    <p className="text-xs text-amber-700">
      Mostrando los {count} prospectos con mayor score. Filtra para ver el resto.
    </p>
  ) : null;
  const showCount = isBoard ? !isLoading : !leadsQuery.isLoading && !showDeletedLeads;

  const manageColumnsButton = (
    className: string,
    content: React.ReactNode,
  ): React.JSX.Element => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <button
              type="button"
              onClick={() => setManageOpen(true)}
              disabled={filtersActive}
              aria-label="Administrar columnas"
              className={`${className} disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {content}
            </button>
          </span>
        </TooltipTrigger>
        {filtersActive && (
          <TooltipContent side="bottom">
            <p>Limpia los filtros para administrar columnas</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );

  const manageLabelsButton = (
    className: string,
    content: React.ReactNode,
  ): React.JSX.Element => (
    <button
      type="button"
      onClick={openManageLabels}
      aria-label="Administrar etiquetas"
      className={className}
    >
      {content}
    </button>
  );

  const newLeadButton = (className: string): React.JSX.Element => (
    <button type="button" onClick={openCreateModal} className={className}>
      + Nuevo lead
    </button>
  );

  const content =
    isError && isBoard ? (
      <p className="px-4 py-12 text-center text-sm text-red-500 md:px-0 md:py-0 md:text-left">
        Error al cargar el pipeline. Intenta de nuevo.
      </p>
    ) : isBoard ? (
      <BoardView stages={stages} grouped={grouped} counts={counts} isLoading={isLoading} />
    ) : (
      <TableView />
    );

  return (
    <LabelsProvider>
      {/*
        Only the page chrome is duplicated per breakpoint — the header reads
        differently on a phone. Everything below it renders ONCE: BoardView,
        TableView and LeadFilters each carry their own mobile and desktop trees,
        so rendering them inside both wrappers mounted every card twice. The CSS
        hid one copy; React still paid for it.
      */}
      <div className="flex flex-1 min-h-0 flex-col md:h-full md:gap-4 md:p-6">
        {/* ── Mobile chrome ─────────────────────────── */}
        <div className="md:hidden">
          <PageHeader
            title="Prospectos"
            subtitle={showCount ? `${count} resultados` : undefined}
            right={
              <div className="flex items-center gap-2">
                {isAdmin && isBoard &&
                  manageColumnsButton(
                    "flex h-8 w-8 items-center justify-center rounded-md border border-ink-200 text-ink-700 cursor-pointer",
                    <Columns3 size={15} />,
                  )}
                {manageLabelsButton(
                  "flex h-8 w-8 items-center justify-center rounded-md border border-ink-200 text-ink-700 cursor-pointer",
                  <Tag size={15} />,
                )}
                {newLeadButton(
                  "flex items-center gap-1 rounded-md bg-lime px-3 py-1.5 text-[12px] font-semibold text-moss cursor-pointer",
                )}
              </div>
            }
          />
          <div className="bg-white border-b border-ink-100 px-4 py-2 shrink-0">
            <ViewModeToggle />
          </div>
        </div>

        {/* ── Desktop chrome ────────────────────────── */}
        <div className="hidden md:flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-bold text-ink-900">
              Pipeline de Prospectos
            </h1>
            {showCount && (
              <Badge variant="secondary" className="text-xs">
                {count}
              </Badge>
            )}
            <ViewModeToggle />
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && isBoard &&
              manageColumnsButton(
                "rounded-md border border-ink-200 px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-050 cursor-pointer",
                "Columnas",
              )}
            {manageLabelsButton(
              "rounded-md border border-ink-200 px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-050 cursor-pointer",
              "Etiquetas",
            )}
            {newLeadButton(
              "flex items-center gap-1.5 rounded-md bg-lime px-4 py-2 text-sm font-semibold text-moss hover:bg-lime-dk cursor-pointer",
            )}
          </div>
        </div>

        {/* Deleted-leads mode replaces the result set entirely, so the filter bar
            would be lying about what's on screen. */}
        {!showDeletedLeads && (
        // The button lives behind the same condition as the filter bar itself:
        // in deleted-leads mode the bar is hidden because it would lie about
        // what is on screen, and that argument is far stronger for something
        // that says "send to the current filters".
        <LeadFilters trailing={<BroadcastButton />} />
      )}

        {isBoard && truncationNotice && (
          <div className="px-4 py-1 md:px-0 md:py-0">{truncationNotice}</div>
        )}

        {content}
      </div>

      {isAdmin && (
        <ManageColumnsModal
          open={manageOpen}
          onClose={() => setManageOpen(false)}
          leadCounts={leadCounts}
        />
      )}

      <CreateLeadModal />
      <EditLeadModal />
    </LabelsProvider>
  );
}
